import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { ArticleCarouselProps } from './contracts';
import ArticleCarouselUI from './ArticleCarouselUI';
import { useArticlesQuery, useInfiniteArticlesQuery } from '@data/articles/useArticlesQuery';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader/SkeletonLoader';
import { PaginatedResponse, Article } from '@repo/types';

export function ArticleCarousel({
  teamFromSlug,
  startDate,
  endDate,
  tags: extraTags,
  tagsMatchMode,
  excludeTags,
  excludeIds,
  filterByTags,
  extraData,
  sort,
  order,
  limit = 6,
  lazyLoad = false,
  ...props
}: ArticleCarouselProps) {
  const [searchParams] = useSearchParams();

  let tags = '';

  if (extraTags) {
    tags = extraTags;
  } else if (filterByTags && searchParams.get('tags')) {
    tags = searchParams.get('tags') as string;
  } else if (teamFromSlug && extraData?.team?.slug) {
    tags = extraData?.team?.slug;
  }

  const queryArgs = {
    startDate: startDate ?? undefined,
    endDate: endDate ?? undefined,
    tags,
    tagsMatchMode: tagsMatchMode ?? undefined,
    limit: limit ?? undefined,
    sort: sort ?? undefined,
    order: order ?? undefined,
    excludeIds: excludeIds ?? undefined,
    excludeTags: excludeTags ?? undefined,
  };

  // Use regular query when lazyLoad is disabled
  const regularQuery = useArticlesQuery({ ...queryArgs, page: 1 }, { enabled: !lazyLoad });

  // Use infinite query when lazyLoad is enabled
  const infiniteQuery = useInfiniteArticlesQuery(queryArgs, { enabled: lazyLoad });

  const isLoading = lazyLoad ? infiniteQuery.isLoading : regularQuery.isLoading;
  const error = lazyLoad ? infiniteQuery.error : regularQuery.error;

  // Flatten all articles from infinite query pages
  const allArticles = useMemo(() => {
    if (!lazyLoad || !infiniteQuery.data) return [];
    return infiniteQuery.data.pages.flatMap((page) => page.items);
  }, [lazyLoad, infiniteQuery.data]);

  // Create a synthetic PaginatedResponse for the UI component
  const paginatedArticles: PaginatedResponse<Article> | undefined = useMemo(() => {
    if (lazyLoad && infiniteQuery.data?.pages && infiniteQuery.data.pages.length > 0) {
      const lastPage = infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1];
      return {
        items: allArticles,
        pagination: lastPage.pagination,
      };
    }
    return regularQuery.data;
  }, [lazyLoad, infiniteQuery.data, allArticles, regularQuery.data]);

  if (isLoading) {
    return (
      <div style={{ display: 'block', width: '100%', height: '100%' }}>
        <SkeletonLoader count={3} />
      </div>
    );
  }

  if (!paginatedArticles || error) {
    return null;
  }

  return (
    <ArticleCarouselUI
      paginatedArticles={paginatedArticles}
      allArticles={allArticles}
      lazyLoad={lazyLoad}
      hasNextPage={infiniteQuery.hasNextPage}
      isFetchingNextPage={infiniteQuery.isFetchingNextPage}
      fetchNextPage={infiniteQuery.fetchNextPage}
      {...props}
    />
  );
}
